import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

const CONFIG = {
  sphereRadiusProp: 150,
  sphereDetail: { latSteps: 10, lonSteps: 18, randomFactor: 0.0 },
  nodeSettings: { sphereNodeBase: 4, sphereNodeRandom: 3 },
  branchSettings: {
    sphereBranchOuter: 1,
    sphereBranchInner: 0.6,
    branchThresholdMultiplier: 0.4,
  },
  diagonalBranchSettings: {
    enabled: true,
    count: 15,
    sphereBranchOuter: 1,
    sphereBranchInner: 0.6,
  },
  internalNodesSettings: {
    enabled: true,
    count: 20,
    minRadius: 1,
    maxRadius: 2,
  },
  internalBranchSettings: {
    enabled: true,
    sphereBranchOuter: 0.5,
    sphereBranchInner: 0.3,
    connectionThresholdMultiplier: 0.3,
  },
  cameraMultiplier: 3.5,
  animationSpeed: 0.5,
  verticalBobbingAmplitude: 10,
  verticalBobbingFrequency: 1,
  transitionDelay: 3000,
  scrollSensitivity: 0.1, // Sensitivity for scroll movement (when Shift is held)
  maxVerticalOffset: 300, // Max vertical movement range
};

function getContainerSize() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return Math.floor(Math.min(w, h) * 0.9);
}

function getGradientColor(y, sphereRadius) {
  const topColor = new THREE.Color(0x3080c1);
  const midColor = new THREE.Color(0x2E7AB9);
  const bottomColor = new THREE.Color(0x246399);
  const t = (y + sphereRadius) / (2 * sphereRadius);
  return t < 0.5
    ? bottomColor.clone().lerp(midColor, t / 0.5)
    : midColor.clone().lerp(topColor, (t - 0.5) / 0.5);
}

function createHollowBranch(pointA, pointB, outerRadius, innerRadius, sphereRadius) {
  const dist = pointA.distanceTo(pointB);
  const shape = new THREE.Shape();
  shape.absarc(0, 0, outerRadius, 0, Math.PI * 2, false);
  const holePath = new THREE.Path();
  holePath.absarc(0, 0, innerRadius, 0, Math.PI * 2, true);
  shape.holes.push(holePath);

  const extrudeSettings = { steps: 1, depth: dist, bevelEnabled: false };
  const branchGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  branchGeo.translate(0, 0, -dist / 2);

  const colorA = getGradientColor(pointA.y, sphereRadius);
  const colorB = getGradientColor(pointB.y, sphereRadius);
  const midColor = colorA.clone().lerp(colorB, 0.5);
  const branchMat = new THREE.MeshPhysicalMaterial({
    color: midColor,
    metalness: 0.6,
    roughness: 0.4,
    clearcoat: 0.5,
    clearcoatRoughness: 0.2,
  });

  const branchMesh = new THREE.Mesh(branchGeo, branchMat);
  branchMesh.position.copy(pointA).lerp(pointB, 0.5);
  const direction = new THREE.Vector3().subVectors(pointB, pointA).normalize();
  const axis = new THREE.Vector3(0, 0, 1);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(axis, direction);
  branchMesh.setRotationFromQuaternion(quaternion);
  return branchMesh;
}

const ThreeSphere = ({ onComplete, noAutoTransition = false, zoomMultiplier = CONFIG.cameraMultiplier, onLoad }) => {
  const mountRef = useRef(null);
  const [containerSize, setContainerSize] = useState(getContainerSize());

  useEffect(() => {
    function handleResize() {
      setContainerSize(getContainerSize());
    }
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    const container = mountRef.current;
    container.innerHTML = '';

    const width = containerSize;
    const height = containerSize;
    const sphereRadius = CONFIG.sphereRadiusProp;
    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
    camera.position.set(0, 0, sphereRadius * zoomMultiplier);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    // --- ORBIT CONTROLS ---
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;

    // Allow panning (move in X/Y screen space) like Revit/Tekla
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    // Zoom is automatically handled by OrbitControls with the wheel (if not prevented)

    // So that we orbit around the center (where our sphere is):
    controls.target.set(0, 0, 0);

    // Track user interaction to pause auto-rotation
    let userIsInteracting = false;
    let interactionTimeout = null;

    const onUserInteractionStart = () => {
      userIsInteracting = true;
      if (interactionTimeout) clearTimeout(interactionTimeout);
    };
    const onUserInteractionEnd = () => {
      // After a brief delay with no user input, auto-rotation resumes
      interactionTimeout = setTimeout(() => {
        userIsInteracting = false;
      }, 2000);
    };

    controls.addEventListener('start', onUserInteractionStart);
    controls.addEventListener('end', onUserInteractionEnd);

    // LIGHTS
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(sphereRadius, sphereRadius, sphereRadius);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    // MAIN GROUP
    const group = new THREE.Group();
    scene.add(group);

    // BUILD OUTER SPHERE NODES
    const sphereNodesPositions = [];
    const { latSteps, lonSteps, randomFactor } = CONFIG.sphereDetail;
    for (let i = 0; i <= latSteps; i++) {
      const theta = (i * Math.PI) / latSteps;
      for (let j = 0; j < lonSteps; j++) {
        const phi = (j * 2 * Math.PI) / lonSteps;
        const rFactor = 1 + (Math.random() - 0.5) * randomFactor;
        const x = sphereRadius * rFactor * Math.sin(theta) * Math.cos(phi);
        const y = sphereRadius * rFactor * Math.cos(theta);
        const z = sphereRadius * rFactor * Math.sin(theta) * Math.sin(phi);
        sphereNodesPositions.push(new THREE.Vector3(x, y, z));
      }
    }

    sphereNodesPositions.forEach((pos) => {
      const nodeRadius = CONFIG.nodeSettings.sphereNodeBase + Math.random() * CONFIG.nodeSettings.sphereNodeRandom;
      const color = getGradientColor(pos.y, sphereRadius);
      const nodeMaterial = new THREE.MeshPhysicalMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.5,
        metalness: 0.8,
        roughness: 0.3,
        clearcoat: 1.0,
        clearcoatRoughness: 0.1,
      });
      const nodeGeo = new THREE.SphereGeometry(nodeRadius, 32, 32);
      const nodeMesh = new THREE.Mesh(nodeGeo, nodeMaterial);
      nodeMesh.position.copy(pos);
      group.add(nodeMesh);
    });

    // BRANCHES BETWEEN NEARBY NODES
    const branchThreshold = sphereRadius * CONFIG.branchSettings.branchThresholdMultiplier;
    for (let i = 0; i < sphereNodesPositions.length; i++) {
      for (let j = i + 1; j < sphereNodesPositions.length; j++) {
        const posA = sphereNodesPositions[i];
        const posB = sphereNodesPositions[j];
        if (posA.distanceTo(posB) < branchThreshold) {
          const branchMesh = createHollowBranch(
            posA,
            posB,
            CONFIG.branchSettings.sphereBranchOuter,
            CONFIG.branchSettings.sphereBranchInner,
            sphereRadius
          );
          group.add(branchMesh);
        }
      }
    }

    // ADD RANDOM DIAGONAL BRANCHES
    if (CONFIG.diagonalBranchSettings.enabled) {
      const { count, sphereBranchOuter, sphereBranchInner } = CONFIG.diagonalBranchSettings;
      const totalNodes = sphereNodesPositions.length;
      for (let i = 0; i < count; i++) {
        const idxA = Math.floor(Math.random() * totalNodes);
        let idxB = Math.floor(Math.random() * totalNodes);
        while (idxB === idxA) {
          idxB = Math.floor(Math.random() * totalNodes);
        }
        const branchMesh = createHollowBranch(
          sphereNodesPositions[idxA],
          sphereNodesPositions[idxB],
          sphereBranchOuter,
          sphereBranchInner,
          sphereRadius
        );
        group.add(branchMesh);
      }
    }

    // ADD INTERNAL NODES
    if (CONFIG.internalNodesSettings.enabled) {
      const { count, minRadius, maxRadius } = CONFIG.internalNodesSettings;
      const internalNodes = [];
      for (let i = 0; i < count; i++) {
        const u = Math.random();
        const theta = Math.acos(2 * Math.random() - 1);
        const phi = 2 * Math.PI * Math.random();
        const r = sphereRadius * Math.cbrt(u);
        const x = r * Math.sin(theta) * Math.cos(phi);
        const y = r * Math.sin(theta) * Math.sin(phi);
        const z = r * Math.cos(theta);
        internalNodes.push(new THREE.Vector3(x, y, z));
      }

      internalNodes.forEach((pos) => {
        const nodeRadius = minRadius + Math.random() * (maxRadius - minRadius);
        const color = getGradientColor(pos.y, sphereRadius);
        const nodeMaterial = new THREE.MeshPhysicalMaterial({
          color,
          emissive: color,
          emissiveIntensity: 0.5,
          metalness: 0.8,
          roughness: 0.3,
          clearcoat: 1.0,
          clearcoatRoughness: 0.1,
        });
        const nodeGeo = new THREE.SphereGeometry(nodeRadius, 16, 16);
        const nodeMesh = new THREE.Mesh(nodeGeo, nodeMaterial);
        nodeMesh.position.copy(pos);
        group.add(nodeMesh);
      });

      if (CONFIG.internalBranchSettings.enabled) {
        const thr = sphereRadius * CONFIG.internalBranchSettings.connectionThresholdMultiplier;
        for (let i = 0; i < internalNodes.length; i++) {
          for (let j = i + 1; j < internalNodes.length; j++) {
            if (internalNodes[i].distanceTo(internalNodes[j]) < thr) {
              const branchMesh = createHollowBranch(
                internalNodes[i],
                internalNodes[j],
                CONFIG.internalBranchSettings.sphereBranchOuter,
                CONFIG.internalBranchSettings.sphereBranchInner,
                sphereRadius
              );
              group.add(branchMesh);
            }
          }
        }
      }
    }

    // Track vertical offset for SHIFT+wheel
    let verticalOffset = 0;

    // Modified wheel event listener:
    //  - If SHIFT is NOT pressed: let OrbitControls handle zoom.
    //  - If SHIFT IS pressed: use the existing vertical offset logic.
    const handleWheel = (event) => {
      if (!event.shiftKey) {
        // Do not prevent default; OrbitControls will handle zoom.
        return;
      }
      // SHIFT is pressed, so do the vertical offset logic
      event.preventDefault(); // Prevent page scroll
      const delta = event.deltaY * CONFIG.scrollSensitivity;
      verticalOffset -= delta; // Move up/down based on scroll direction
      verticalOffset = THREE.MathUtils.clamp(
        verticalOffset,
        -CONFIG.maxVerticalOffset,
        CONFIG.maxVerticalOffset
      );
      // Apply offset + preserve bobbing in the animate loop
    };

    renderer.domElement.addEventListener('wheel', handleWheel);

    const clock = new THREE.Clock();

    function animate() {
      requestAnimationFrame(animate);
      const delta = clock.getDelta();

      // If user isn't interacting, smoothly rotate around Y
      if (!userIsInteracting) {
        group.rotation.y += CONFIG.animationSpeed * delta;
      }

      // Update group position with scroll offset and bobbing
      group.position.y =
        verticalOffset +
        Math.sin(clock.getElapsedTime() * CONFIG.verticalBobbingFrequency) *
        CONFIG.verticalBobbingAmplitude;

      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    // Simulate load completion
    setTimeout(() => {
      if (onLoad) onLoad();
    }, 1000);

    let timer;
    if (!noAutoTransition && onComplete) {
      timer = setTimeout(() => {
        onComplete();
      }, CONFIG.transitionDelay);
    }

    return () => {
      if (timer) clearTimeout(timer);
      renderer.domElement.removeEventListener('wheel', handleWheel);
      controls.removeEventListener('start', onUserInteractionStart);
      controls.removeEventListener('end', onUserInteractionEnd);
      controls.dispose();
      renderer.dispose();
    };
  }, [onComplete, noAutoTransition, containerSize, zoomMultiplier, onLoad]);

  return (
    <div
      ref={mountRef}
      style={{
        width: `${containerSize}px`,
        height: `${containerSize}px`,
        margin: '0 auto',
        backgroundColor: 'transparent',
      }}
    />
  );
};

export default ThreeSphere;
